import { AbstractMesh, Animation, Vector3 } from '@babylonjs/core';
import { recordMicrophoneAsync } from './recordMicrophoneAsync';
import { RECORD_DURATION_MS, WEBHOOK_URL, TORUS_SCALE } from '../utils/constants';
import { getSessionId } from '../utils/cookies';
import { getChannelName } from '../supabase/mediaSubscription';
import { transitionTorusColor, TorusState } from '../scene/torusStateManager';

export interface PausableSound {
  pause(): void;
  play(): void;
  isPlaying: boolean;
}

let isRecording = false;

function startPulse(mesh: AbstractMesh): void {
  const base = TORUS_SCALE;
  const peak = TORUS_SCALE * 1.25;

  const anim = new Animation(
    'hollaPulse',
    'scaling',
    30,
    Animation.ANIMATIONTYPE_VECTOR3,
    Animation.ANIMATIONLOOPMODE_CYCLE
  );

  anim.setKeys([
    { frame: 0, value: new Vector3(base, base, base) },
    { frame: 15, value: new Vector3(peak, peak, peak) },
    { frame: 30, value: new Vector3(base, base, base) },
  ]);

  mesh.animations = [anim];
  mesh.getScene().beginAnimation(mesh, 0, 30, true);
}

function stopPulse(mesh: AbstractMesh): void {
  mesh.getScene().stopAnimation(mesh);
  mesh.animations = [];
  mesh.scaling = new Vector3(TORUS_SCALE, TORUS_SCALE, TORUS_SCALE);
}

/**
 * Handles the HollaSphere pick event.
 * Pulses the orb, mutes sounds, records mic audio, uploads WAV, then resumes.
 */
export async function handleHollaPickAsync(
  hollaSphere: AbstractMesh,
  managedSounds: PausableSound[],
  userUuid: string
): Promise<void> {
  if (isRecording) {
    console.warn('[HollaPick] Recording already in progress, ignoring click.');
    return;
  }

  isRecording = true;
  console.log('[HollaPick] Starting recording sequence...');

  // Track which sounds were actually paused so we can resume only those
  const pausedSounds: PausableSound[] = [];

  try {
    // Visual feedback: transition to recording color and pulse the orb
    await transitionTorusColor(hollaSphere, TorusState.RECORDING);
    startPulse(hollaSphere);

    // Mute all managed sounds (only if actually playing)
    for (const sound of managedSounds) {
      if (sound.isPlaying) {
        sound.pause();
        pausedSounds.push(sound);
      }
    }

    // Record microphone
    const wavBlob = await recordMicrophoneAsync(RECORD_DURATION_MS);
    console.log(`[HollaPick] Recording complete. Size: ${wavBlob.size} bytes`);

    // Transition to uploading state
    stopPulse(hollaSphere);
    await transitionTorusColor(hollaSphere, TorusState.UPLOADING);

    // Upload to webhook
    const formData = new FormData();
    formData.append('audio', wavBlob, 'recording.wav');

    const response = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'X-Session-Id': getSessionId() || '',
        'X-User-Id': userUuid,
        'X-Channel': getChannelName(userUuid),
      },
      body: formData,
    });

    if (!response.ok) {
      console.error(`[HollaPick] Upload failed: ${response.status} ${response.statusText}`);
      await transitionTorusColor(hollaSphere, TorusState.ERROR);
      await new Promise(resolve => setTimeout(resolve, 1500)); // Show error for 1.5s
    } else {
      console.log('[HollaPick] Upload successful:', response.status);
    }
  } catch (error) {
    console.error('[HollaPick] Error during recording sequence:', error);
    stopPulse(hollaSphere);
    await transitionTorusColor(hollaSphere, TorusState.ERROR);
    await new Promise(resolve => setTimeout(resolve, 1500)); // Show error for 1.5s
  } finally {
    // Always stop pulse, transition back to idle, resume sounds, and reset flag
    stopPulse(hollaSphere);
    await transitionTorusColor(hollaSphere, TorusState.IDLE);
    for (const sound of pausedSounds) {
      sound.play();
    }
    isRecording = false;
  }
}
